import React, { FC, useCallback, useMemo } from 'react';

import { LicenseInfoTypeFragment, LicensePaymentState, LicenseType } from 'domain/billing/types';

import LicensesListItem from '../LicensesListItem';
import { LicensesModalAction } from '../../types';

import styles from './index.module.scss';

export type ModalLicenseType = LicenseInfoTypeFragment & {
  isChecked: boolean;
};

interface LicensesListProps {
  totalLicenses: LicenseType[];
  toSuspendAutorenewal: string[];
  toEnableAutorenewal: string[];
  isAllLicensesDisabled: boolean;
  dispatch: (value: LicensesModalAction) => void;
  setSubscriptionCost: (licensesIds: string[]) => Promise<void>;
}

const LicensesList: FC<LicensesListProps> = ({
  totalLicenses,
  toSuspendAutorenewal,
  toEnableAutorenewal,
  isAllLicensesDisabled,
  dispatch,
  setSubscriptionCost,
}) => {
  const getModalLicenseItem = useCallback(
    (item: LicenseInfoTypeFragment): ModalLicenseType => {
      const isInSuspendAutorenewal = toSuspendAutorenewal.indexOf(item.id) > -1;
      const isInEnableAutorenewal = toEnableAutorenewal.indexOf(item.id) > -1;
      const isChecked = isInEnableAutorenewal || (isInSuspendAutorenewal ? false : item.autoRenewal);

      return {
        ...item,
        isChecked,
      };
    },
    [toSuspendAutorenewal, toEnableAutorenewal]
  );

  const licenses = useMemo(() => totalLicenses?.map(getModalLicenseItem), [totalLicenses, getModalLicenseItem]);

  const onChange = useCallback(
    (item: LicenseInfoTypeFragment, value: boolean) => {
      if (item.autoRenewal && value) {
        const suspend = toSuspendAutorenewal.filter((id) => id !== item.id);
        dispatch({ type: 'setToSuspendAutorenewal', toSuspendAutorenewal: suspend });
      }

      if (item.autoRenewal && !value) {
        const suspend = [...toSuspendAutorenewal.filter((id) => id !== item.id), item.id];
        dispatch({ type: 'setToSuspendAutorenewal', toSuspendAutorenewal: suspend });
      }

      if (!item.autoRenewal && value) {
        const enable = [...toEnableAutorenewal.filter((id) => id !== item.id), item.id];
        dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: enable });
        setSubscriptionCost(enable);
      }

      if (!item.autoRenewal && !value) {
        const enable = toEnableAutorenewal.filter((id) => id !== item.id);
        dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: enable });
        setSubscriptionCost(enable);
      }
    },
    [toSuspendAutorenewal, toEnableAutorenewal]
  );

  return (
    <div className={styles.licensesList}>
      {licenses?.map((item) => (
        <LicensesListItem
          item={item}
          key={`licenses_list_item_${item.id}`}
          isChecked={item.isChecked}
          isDisabled={isAllLicensesDisabled}
          isGraceSubtitleVisible={item.paymentState === LicensePaymentState.Grace}
          isWaitPaymentSubtitleVisible={item.paymentState === LicensePaymentState.WaitPayment}
          onChange={(value) => onChange(item, value)}
        />
      ))}
    </div>
  );
};

export default React.memo(LicensesList);
